import { Document } from "../model/document.types"
import { NotificationType } from "@/entities/notification/model/notification.types"
import { Establishment } from "@/entities/establishment/model/establishment.types"

const MONTHS_BEFORE_DUERP_OUTDATED = 12
export type DocumentOutdatedNotificationType = Extract<NotificationType, "duerp-missing" | "duerp-outdated">
export type DocumentOutdatedRecommendation = {
  type: DocumentOutdatedNotificationType
  establishmentId: string
}

function getDocumentDate(document: Document) {
  const date = new Date(document.date)
  return Number.isNaN(date.getTime()) ? null : date
}

function getLatestDocument(documents: Document[], type: Document["type"]) {
  return [...documents]
    .filter((document) => document.type === type && getDocumentDate(document))
    .sort((a, b) => getDocumentDate(b)!.getTime() - getDocumentDate(a)!.getTime())[0]
}

function subtractMonths(date: Date, months: number) {
  const result = new Date(date)
  result.setMonth(result.getMonth() - months)
  return result
}

export function getDocumentOutdatedNotificationTypes(
  documents: Document[],
  currentDate = new Date()
): DocumentOutdatedNotificationType[] {
  const latestDuerp = getLatestDocument(documents, "duerp")
  const duerpLimitDate = subtractMonths(currentDate, MONTHS_BEFORE_DUERP_OUTDATED)
  const latestDuerpDate = latestDuerp ? getDocumentDate(latestDuerp) : null
  const types: DocumentOutdatedNotificationType[] = []

  if (!latestDuerpDate) {
    types.push("duerp-missing")
  } else if (latestDuerpDate < duerpLimitDate) {
    types.push("duerp-outdated")
  }

  return types
}

export function isDocumentOutdated(documents: Document[], currentDate = new Date()) {
  return getDocumentOutdatedNotificationTypes(documents, currentDate).length > 0
}

export function getOutdatedByEstablishment(establishments: Establishment[], currentDate = new Date()) {
  return establishments.reduce<DocumentOutdatedRecommendation[]>((acc, establishment) => {
    if (establishment.isDisabled) return acc

    const documents = establishment.lastDuerp
      ? [
          {
            id: `${establishment.id}-last-duerp`,
            type: "duerp" as const,
            date: new Date(establishment.lastDuerp),
            EstablishmentId: establishment.id,
            url: null,
            content: {}
          }
        ]
      : []
    const notificationTypes = getDocumentOutdatedNotificationTypes(documents, currentDate)

    notificationTypes.forEach((notificationType) => {
      acc.push({
        type: notificationType,
        establishmentId: establishment.id
      })
    })

    return acc
  }, [])
}
